import { useCallback, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { Copy, Landmark, Loader2, Search, Upload } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { api, errMsg } from "@/lib/api";
import { fmtDateTime, rupiah } from "@/lib/format";
import { useSettings } from "@/context/SettingsContext";
import { PaymentBadge } from "@/components/Badges";
import { useAuth } from "@/context/AuthContext";

function Instructions() {
  const { settings } = useSettings();
  return (
    <div className="space-y-8">
      <div className="space-y-3">
        {(settings?.bank_accounts || []).map((b, i) => (
          <div key={`${b.bank}-${i}`} className="flex items-center justify-between rounded-lg border border-slate-200 p-4 bg-white">
            <div className="flex items-center gap-3">
              <Landmark className="h-5 w-5 text-amber-500" />
              <div>
                <p className="text-sm font-semibold text-slate-900">{b.bank}</p>
                <p className="font-mono text-sm text-slate-700">{b.number}</p>
                <p className="text-xs text-slate-500">a.n. {b.holder}</p>
              </div>
            </div>
            <Button variant="ghost" size="icon" onClick={() => { navigator.clipboard?.writeText(b.number); toast.success("Nomor rekening disalin"); }} data-testid="copy-bank-account-button"><Copy className="h-4 w-4" /></Button>
          </div>
        ))}
      </div>
      <ol className="space-y-4">
        {(settings?.payment_steps || []).map((s, i) => (
          <li key={`${i}-${s}`} className="flex gap-4"><span className="font-mono text-xs text-amber-600 mt-0.5">{String(i + 1).padStart(2, "0")}</span><span className="text-sm text-slate-600">{s}</span></li>
        ))}
      </ol>
    </div>
  );
}

function ProofForm({ payment, email, onDone }) {
  const [file, setFile] = useState(null);
  const [sender, setSender] = useState({ sender_name: "", sender_bank: "" });
  const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault();
    if (!file) return toast.error("Pilih file bukti pembayaran");
    const fd = new FormData();
    fd.append("invoice", payment.invoice);
    fd.append("email", email);
    fd.append("sender_name", sender.sender_name);
    fd.append("sender_bank", sender.sender_bank);
    fd.append("file", file);
    setBusy(true);
    try {
      const { data } = await api.post("/payments/proof", fd);
      toast.success("Bukti pembayaran terkirim. Menunggu verifikasi admin.");
      onDone(data);
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setBusy(false);
    }
  };
  return (
    <form onSubmit={submit} className="space-y-4 border-t border-slate-200 pt-6 mt-6" data-testid="payment-proof-form">
      <p className="font-display font-semibold">Unggah bukti pembayaran</p>
      <div className="grid sm:grid-cols-2 gap-4">
        <div className="space-y-2"><Label>Nama pengirim</Label><Input value={sender.sender_name} onChange={(e) => setSender({ ...sender, sender_name: e.target.value })} data-testid="proof-sender-name-input" /></div>
        <div className="space-y-2"><Label>Bank pengirim</Label><Input value={sender.sender_bank} onChange={(e) => setSender({ ...sender, sender_bank: e.target.value })} data-testid="proof-sender-bank-input" /></div>
      </div>
      <Input type="file" accept="image/png,image/jpeg,image/webp,application/pdf" onChange={(e) => setFile(e.target.files?.[0] || null)} data-testid="proof-file-input" />
      <Button type="submit" disabled={busy} className="bg-slate-900" data-testid="proof-submit-button">
        {busy ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Upload className="mr-2 h-4 w-4" />}Kirim Bukti
      </Button>
    </form>
  );
}

function PaymentStatus({ payment, email, onUpdate }) {
  const canUpload = ["menunggu_pembayaran", "ditolak"].includes(payment.status);
  return (
    <div className="rounded-lg border border-slate-200 bg-white p-6" data-testid="payment-status-card">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <p className="text-xs uppercase tracking-widest text-slate-500">Invoice</p>
          <p className="font-mono text-lg font-bold text-slate-900" data-testid="payment-invoice">{payment.invoice}</p>
        </div>
        <PaymentBadge status={payment.status} />
      </div>
      <dl className="mt-6 grid grid-cols-2 gap-4 text-sm">
        <div><dt className="text-slate-500">Paket</dt><dd className="font-semibold">{payment.package_name}</dd></div>
        <div><dt className="text-slate-500">Nominal</dt><dd className="font-display text-xl font-extrabold" data-testid="payment-amount">{rupiah(payment.amount)}</dd></div>
        <div><dt className="text-slate-500">Dibuat</dt><dd>{fmtDateTime(payment.created_at)}</dd></div>
        <div><dt className="text-slate-500">Bukti</dt><dd>{payment.proof_file_id ? "Sudah diunggah" : "Belum ada"}</dd></div>
      </dl>
      {payment.note && <p className="mt-4 text-sm rounded bg-slate-50 p-3 text-slate-700">Catatan admin: {payment.note}</p>}
      {canUpload && <ProofForm payment={payment} email={email} onDone={onUpdate} />}
    </div>
  );
}

export default function Payment() {
  const [params] = useSearchParams();
  const { user } = useAuth();
  const [form, setForm] = useState({ invoice: params.get("invoice") || "", email: "" });
  const [payment, setPayment] = useState(null);
  const [busy, setBusy] = useState(false);

  const lookup = useCallback(async (invoice, email) => {
    setBusy(true);
    try {
      const { data } = await api.post("/payments/lookup", { invoice, email });
      setPayment(data);
    } catch (e) {
      setPayment(null);
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  }, []);

  useEffect(() => {
    if (user?.email) {
      setForm((f) => ({ ...f, email: user.email }));
      const inv = params.get("invoice");
      if (inv) lookup(inv, user.email);
    }
  }, [user, params, lookup]);

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 lg:py-20" data-testid="payment-page">
      <p className="eyebrow">Pembayaran</p>
      <h1 className="mt-4 text-4xl sm:text-5xl font-extrabold tracking-tight text-slate-900">Instruksi & status pembayaran</h1>
      <div className="mt-12 grid lg:grid-cols-12 gap-10">
        <div className="lg:col-span-5"><Instructions /></div>
        <div className="lg:col-span-7 space-y-6">
          <form onSubmit={(e) => { e.preventDefault(); lookup(form.invoice, form.email); }} className="rounded-lg bg-slate-50 border border-slate-200 p-6 grid sm:grid-cols-[1fr_1fr_auto] gap-3 items-end" data-testid="payment-lookup-form">
            <div className="space-y-2"><Label>Nomor invoice</Label><Input required value={form.invoice} onChange={(e) => setForm({ ...form, invoice: e.target.value })} placeholder="ADH-..." data-testid="lookup-invoice-input" /></div>
            <div className="space-y-2"><Label>Email pendaftar</Label><Input required type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="lookup-email-input" /></div>
            <Button type="submit" disabled={busy} className="bg-slate-900" data-testid="lookup-submit-button">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Search className="h-4 w-4" />}<span className="ml-2">Cek</span></Button>
          </form>
          {payment && <PaymentStatus payment={payment} email={form.email} onUpdate={setPayment} />}
        </div>
      </div>
    </div>
  );
}
